/* global document */

import type { TraceTokenData } from '../../token/trace-token-data.model';
import type { TaskPaneOfficeAuthFlow } from '../auth-flow';
import type { ReportingTaskPaneFilters } from '../reporting-filters';
import type { TaskPaneUiSessionState } from '../session/task-pane-ui-session.state';

/** Token read surface for this module (supports tests with fakes). */
export interface TaskPaneChromeTokenPort {
    getTokenData(): TraceTokenData | null;
    hasConnectedSession(): boolean;
    clearIncompleteSessionIfNeeded(): void;
}

export interface TaskPaneChromeSyncDeps {
    tokenStore: TaskPaneChromeTokenPort;
    session: Pick<
        TaskPaneUiSessionState,
        'setSwitchOrganisationFlowActive' | 'isSwitchOrganisationFlowActive'
    >;
    userInfoStore: { clear(): void };
    getPersistedOrgCodeForTitle: () => string;
    setOrgCodeInputDisabled: (disabled: boolean) => void;
    clearOrgCodeFieldValidation: () => void;
    logAuthDebug: (event: string, payload: Record<string, unknown>) => void;
    reporting: Pick<
        ReportingTaskPaneFilters,
        | 'resetReportingUi'
        | 'renderPanelsForCurrentTarget'
        | 'resetTabularWriteSession'
        | 'syncRefreshButtonState'
    >;
    officeAuth: Pick<TaskPaneOfficeAuthFlow, 'clearAuthStatusDetail'>;
    labels: {
        switchOrganisation: string;
        continueSwitchOrganisation: string;
        connectSignIn: string;
    };
}

interface ChromeAuthContext {
    tokenData: TraceTokenData | null;
    accessToken: string | undefined;
    /** Full session: access + refresh token (renewable). */
    isConnected: boolean;
    compactConnected: boolean;
    showDataSection: boolean;
    showSwitchOrgCancel: boolean;
}

function readChromeAuthContext(deps: TaskPaneChromeSyncDeps): ChromeAuthContext {
    const tokenData = deps.tokenStore.getTokenData();
    const accessToken = tokenData?.accessToken;
    const isConnected = deps.tokenStore.hasConnectedSession();

    if (!isConnected) {
        deps.session.setSwitchOrganisationFlowActive(false);
    }

    const switchFlowActive = deps.session.isSwitchOrganisationFlowActive();
    const compactConnected = isConnected && !switchFlowActive;
    const showDataSection = isConnected && !switchFlowActive;

    return {
        tokenData,
        accessToken,
        isConnected,
        compactConnected,
        showDataSection,
        showSwitchOrgCancel: isConnected && switchFlowActive,
    };
}

function applyDataSection(
    ctx: ChromeAuthContext,
    deps: TaskPaneChromeSyncDeps
): { dataSection: HTMLElement | null; fetchButton: HTMLButtonElement | null } {
    const dataSection = document.getElementById('data-section');
    const fetchButton = document.getElementById('fetch-data') as HTMLButtonElement | null;

    if (dataSection) {
        dataSection.classList.toggle('hidden', !ctx.showDataSection);
        if (!ctx.isConnected) {
            deps.reporting.resetReportingUi({ clearSummary: true });
            deps.reporting.resetTabularWriteSession();
        }
    }

    return { dataSection, fetchButton };
}

function logChromeSync(
    ctx: ChromeAuthContext,
    dataSection: HTMLElement | null,
    fetchButton: HTMLButtonElement | null,
    deps: TaskPaneChromeSyncDeps
): void {
    deps.logAuthDebug('syncTaskPaneChromeFromAuth', {
        isConnected: ctx.isConnected,
        accessTokenChars: ctx.accessToken?.length ?? 0,
        refreshTokenChars: ctx.tokenData?.refreshToken?.length ?? 0,
        dataSectionFound: Boolean(dataSection),
        dataSectionHasHiddenClass: dataSection?.classList.contains('hidden') ?? null,
        fetchButtonDisabled: fetchButton?.disabled ?? null,
    });
}

function applyMainCardTitle(ctx: ChromeAuthContext, deps: TaskPaneChromeSyncDeps): void {
    const mainTitle = document.getElementById('main-card-title');
    if (!mainTitle) {
        return;
    }
    if (ctx.compactConnected) {
        const orgTitle = deps.getPersistedOrgCodeForTitle();
        mainTitle.textContent = orgTitle;
        mainTitle.setAttribute('aria-label', `Organization code ${orgTitle}`);
    } else {
        mainTitle.textContent = 'Trace Reports';
        mainTitle.removeAttribute('aria-label');
    }
}

function applyLoginChrome(ctx: ChromeAuthContext): void {
    document
        .getElementById('trace-card-subtitle')
        ?.classList.toggle('hidden', ctx.compactConnected);
    document
        .getElementById('login-section-org-block')
        ?.classList.toggle('hidden', ctx.compactConnected);
    document
        .getElementById('switch-org-cancel-button')
        ?.classList.toggle('hidden', !ctx.showSwitchOrgCancel);
    document.getElementById('auth-connected-dot')?.classList.toggle('hidden', !ctx.isConnected);
}

function applyAuthStatus(ctx: ChromeAuthContext, deps: TaskPaneChromeSyncDeps): void {
    const authStatus = document.getElementById('auth-status');
    if (!authStatus) {
        return;
    }
    authStatus.classList.remove('hint--success', 'hint--error');
    if (ctx.isConnected) {
        deps.officeAuth.clearAuthStatusDetail();
        authStatus.textContent = 'Connected';
        authStatus.classList.add('hint--success');
    } else {
        authStatus.textContent = 'Not connected';
    }
}

function applyFetchButtons(ctx: ChromeAuthContext, deps: TaskPaneChromeSyncDeps): void {
    const fetchButton = document.getElementById('fetch-data') as HTMLButtonElement | null;
    if (fetchButton) {
        fetchButton.disabled = !ctx.isConnected;
    }
    if (ctx.isConnected) {
        deps.reporting.syncRefreshButtonState();
    } else {
        const refreshButton = document.getElementById(
            'refresh-report-data'
        ) as HTMLButtonElement | null;
        if (refreshButton) {
            refreshButton.disabled = true;
        }
    }
}

function applyConnectButton(ctx: ChromeAuthContext, deps: TaskPaneChromeSyncDeps): void {
    const connectButton = document.getElementById('connect-button') as HTMLButtonElement | null;
    if (!connectButton) {
        return;
    }
    if (ctx.compactConnected) {
        connectButton.textContent = deps.labels.switchOrganisation;
        connectButton.setAttribute(
            'aria-label',
            'Switch organization. You can cancel to stay signed in to the current one.'
        );
    } else if (ctx.showSwitchOrgCancel) {
        connectButton.textContent = deps.labels.continueSwitchOrganisation;
        connectButton.setAttribute(
            'aria-label',
            'Continue to sign in with the organization code above. Signs out the current organization first.'
        );
    } else {
        connectButton.textContent = deps.labels.connectSignIn;
        connectButton.setAttribute('aria-label', 'Connect to Trace with your account.');
    }
    const continueUsesPrimaryStyle = ctx.showSwitchOrgCancel;
    connectButton.classList.toggle('trace-btn--primary', continueUsesPrimaryStyle);
    connectButton.classList.toggle('trace-btn--secondary', !continueUsesPrimaryStyle);
}

function applyOrgFieldAndDisconnectedState(
    ctx: ChromeAuthContext,
    deps: TaskPaneChromeSyncDeps
): void {
    deps.setOrgCodeInputDisabled(ctx.compactConnected);
    if (ctx.isConnected) {
        deps.clearOrgCodeFieldValidation();
    }
    if (!ctx.isConnected) {
        deps.userInfoStore.clear();
        deps.reporting.renderPanelsForCurrentTarget();
    }
}

export function syncTaskPaneChromeWithAuthState(deps: TaskPaneChromeSyncDeps): void {
    deps.tokenStore.clearIncompleteSessionIfNeeded();
    const ctx = readChromeAuthContext(deps);
    const { dataSection, fetchButton } = applyDataSection(ctx, deps);
    logChromeSync(ctx, dataSection, fetchButton, deps);
    applyMainCardTitle(ctx, deps);
    applyLoginChrome(ctx);
    applyAuthStatus(ctx, deps);
    applyFetchButtons(ctx, deps);
    applyConnectButton(ctx, deps);
    applyOrgFieldAndDisconnectedState(ctx, deps);
}
